const isIframeEditor = (): boolean => {
	const body = document.querySelector('body');
	if (!body) return false;
	return body.classList.contains('block-editor-iframe__body');
};
const LOG_PREFIX: string = isIframeEditor() ? '[module-loader-iframe]' : '[module-loader-admin]';
//console.log(LOG_PREFIX, 'loaded');

declare const __USE_VISUAL_EDITOR__: boolean;

import { PageEvent } from '../utils/barba/BarbaController';
import { Component, ComponentEvent } from '../components/Component';
import { toKebabCase } from '../utils/kebab-case';
import { addBlockId, blockLifecycleController } from '../controllers/block-lifecycle.controller';

const DEBUG_VERBOSE = false; //isIframeEditor();
const prefix = 'cds';
const blocktypeQuery: string = '[cds-blocktype]';
export type BlockHandlerFunc = (element: HTMLElement, isCms: boolean) => void;

//const webAwesomeModules = import.meta.glob('/node_modules/@awesome.me/webawesome/dist/components/*/*.js');
/*const importWebAwesomeModule = async (tagName: string) => {
	// Get the component name without the "wa-" prefix
	const waTagName = tagName.replace('wa-', '');
	// Dynamically import the component code from webawesome
	const key: string = `/node_modules/@awesome.me/webawesome/dist/components/${waTagName}/${waTagName}.js`;
	const moduleLoader: any = webAwesomeModules[key];
	await moduleLoader();
};*/

//const featureModules = import.meta.glob('../blocks/feature/**/index.ts');

const coreBlockModules = Object.entries(import.meta.glob('../blocks/core/*/*.ts', { eager: false }))
	.filter(([path]) => {
		const match = path.match(/\/([^/]+)\/\1\.ts$/); // Matches /[dirname]/[dirname].ts
		return !!match;
	})
	.reduce(
		(acc, [path, mod]) => {
			acc[path] = mod;
			return acc;
		},
		{} as Record<string, any>
	);

const featureModules = Object.entries(import.meta.glob('../blocks/feature/*/*.ts', { eager: false }))
	.filter(([path]) => {
		const match = path.match(/\/([^/]+)\/\1\.ts$/); // Matches /[dirname]/[dirname].ts
		return !!match;
	})
	.reduce(
		(acc, [path, mod]) => {
			acc[path] = mod;
			return acc;
		},
		{} as Record<string, any>
	);

const coreComponentModules = Object.entries(import.meta.glob('../components/*/*.ts', { eager: false }))
	.filter(([path]) => {
		const match = path.match(/\/([^/]+)\/\1\.ts$/);
		return !!match;
	})
	.reduce(
		(acc, [path, mod]) => {
			acc[path] = mod;
			return acc;
		},
		{} as Record<string, any>
	);

const modules = { ...featureModules, ...coreComponentModules, ...coreBlockModules };

const tagToLoaderMap: Record<string, () => Promise<any>> = {};

for (const path in modules) {
	//const match = path.match(/\.{1,2}\/blocks\/feature\/([^/]+)\/index\.ts$/);
	let match = path.match(/\.{1,2}\/blocks\/feature\/([^/]+)\/\1\.ts$/); // matches [dirname]/[dirname].ts
	if (match) {
		const kebabName = toKebabCase(match[1]);
		const tag = `${prefix}-${kebabName}`;
		tagToLoaderMap[tag] = modules[path];
		//DEBUG_VERBOSE && console.log(LOG_PREFIX, modules[path]);
	}
	if (!match) {
		match = path.match(/\.{1,2}\/components\/([^/]+)\/\1\.ts$/);
		if (match) {
			const kebabName = toKebabCase(match[1]);
			const tag = `${prefix}-${kebabName}`;
			tagToLoaderMap[tag] = modules[path];
			//DEBUG_VERBOSE && console.log(LOG_PREFIX, modules[path]);
		}
	}

	// For the core blocks, map as follows:
	// cds-blocktype-cta to ../blocks/core/CTA/CTA.ts
	// cds-blocktype-carousel to ../blocks/core/Carousel/Carousel.ts
	// cds-image-text to ../components/ImageText/ImageText.ts
	if (!match) {
		match = path.match(/\.{1,2}\/blocks\/core\/([^/]+)\/\1\.ts$/);
		if (match) {
			const kebabName = toKebabCase(match[1]);
			//const tag = `${prefix}-blocktype-${kebabName}`;
			tagToLoaderMap[kebabName] = modules[path];
		}
	}

	if (!match) {
		DEBUG_VERBOSE && console.warn(LOG_PREFIX, `No matching tag found for path: ${path}`);
	}
}

const selector = Object.keys(tagToLoaderMap).join(',');

export const loadComponentCodeForTag = (tag: string) => {
	if (!tag) return;
	tag = tag.toLowerCase();
	if (customElements.get(tag)) {
		DEBUG_VERBOSE && console.log(LOG_PREFIX, `Component <${tag}> already defined, skipping load.`);
		return;
	}
	if (tagToLoaderMap[tag]) {
		DEBUG_VERBOSE && console.log(LOG_PREFIX, 'loadComponentCodeForTag:', tag);
		tagToLoaderMap[tag]?.()
			.then(() => DEBUG_VERBOSE && console.log(LOG_PREFIX, `Loaded component: <${tag}>`))
			.catch((err) => DEBUG_VERBOSE && console.error(LOG_PREFIX, `Failed to load <${tag}>:`, err));
	}
};

export const scanDomForComponentTags = (rootElement: Document | ShadowRoot | HTMLElement = document): Set<string> => {
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'scanDomForComponentTags');
	// Get the kebab-case names of all supported core components
	const coreKeys = Object.keys(coreComponentModules).map((key) => key.split('/').pop()?.replace('.ts', ''));
	const featureKeys = Object.keys(featureModules).map((key) => key.split('/').pop()?.replace('.ts', ''));
	const allKeys = [...coreKeys, ...featureKeys];
	const kebabKeys = allKeys.map((key) => `${prefix}-${toKebabCase(key || '')}`);
	// Find all elements in the DOM that use our prefix
	const viableElements = Array.from(rootElement.querySelectorAll('*')).filter((el) => el.tagName.toLowerCase().startsWith(prefix + '-'));

	// Find all core-component elements in the DOM
	const elements = viableElements.filter((el: Element) => kebabKeys.some((key) => key == el.tagName.toLowerCase()));

	// Get the names of those core-component elements
	const elementNames = elements.map((el: Element) => el.tagName.toLowerCase());

	// Remove duplicates
	const uniqueElementNames = new Set(elementNames);

	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'Custom components in library:', kebabKeys.join(', '));
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'Custom elements found in DOM:', Array.from(uniqueElementNames).join(', '));

	return uniqueElementNames;
};

export const scanDomForWebAwesomeTags = (rootElement: Document | ShadowRoot | HTMLElement = document): Set<string> => {
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'scanDomForWebAwesomeTags');
	// Find all elements in the DOM that use the webawesome prefix
	const onlyUndefined: boolean = false;
	const selector: string = onlyUndefined ? '*:not(:defined)' : '*';
	const foundElements = Array.from(rootElement.querySelectorAll(selector)).filter((el) => el.tagName.toLowerCase().startsWith('wa-'));
	// Get the names of those elements
	const elementNames = foundElements.map((el: Element) => el.tagName.toLowerCase());
	// Remove duplicates
	const uniqueElementNames = new Set(elementNames);
	DEBUG_VERBOSE && console.log(LOG_PREFIX, 'WebAwesome elements found in DOM:', Array.from(uniqueElementNames).join(', '));
	return uniqueElementNames;
};

interface LoadRequiredModulesParams {
	pageElement?: HTMLElement;
	mimicPageEnter?: boolean;
	mimicPageEnterComplete?: boolean;
}

const loadRequiredModules = async (_logNote?: string, params: LoadRequiredModulesParams = {}) => {
	if (_logNote && DEBUG_VERBOSE) console.log(LOG_PREFIX, 'loadRequiredModules (' + _logNote + ')');
	// Load the component code for each unique core element found in the DOM
	scanDomForComponentTags().forEach((tag) => loadComponentCodeForTag(tag));

	// Load the WebAwesome modules for each unique WA element found in the DOM
	//scanDomForWebAwesomeTags().forEach(async (tag) => await importWebAwesomeModule(tag));

	// Find all elements in the dom with an attribute named cds-blocktype
	// and attempt to trigger their block handler code.
	const target = params.pageElement || document;
	const blockElements = Array.from(target.querySelectorAll<HTMLElement>(blocktypeQuery));
	for (var i = 0; i < blockElements.length; i++) {
		let name = blockElements[i].getAttribute('cds-blocktype');
		if (!name) name = blockElements[i].tagName.toLowerCase();
		await processElementBlocktype(blockElements[i]);
		convertClassnameAttributes();
	}
	params.mimicPageEnter && blockLifecycleController.mimicPageEnter();
	params.mimicPageEnterComplete && blockLifecycleController.mimicPageEnterComplete();
};

const processElementBlocktype = async (element: HTMLElement, firePageEnter: boolean = false): Promise<void> => {
	const blockType: string | null = element.getAttribute('cds-blocktype');
	//console.log(LOG_PREFIX, 'Processing block type:', blockType, 'for element:', element);
	if (!blockType) return Promise.resolve();
	const loader: any = tagToLoaderMap[blockType];
	if (!loader) return Promise.resolve();
	return new Promise((resolve) => {
		loader()
			.then((result: any) => {
				//const blockHandler = result['handler'];
				const parent = element.parentElement;
				if (!parent) return;
				addBlockId(element);

				// In CMS, blocks seem to be re-initialised as the editor is starting up.
				// Ensure we don't set up more than one set of callbacks for a block.
				isCms && blockLifecycleController.clearBlockCallbacks(element);

				blockLifecycleController.addBlockCallbacks(
					element,
					isCms,
					result['onPageEnter'],
					result['onPageEnterComplete'],
					result['onPageLeave'],
					result['onPageLeaveComplete']
				);

				if (firePageEnter) {
					blockLifecycleController.mimicPageEnter(element);
					blockLifecycleController.mimicPageEnterComplete(element);
				}

				//isCms && postProcessBlockForCms(element);

				activeBlocks.add(element);

				//blockHandler && typeof blockHandler === 'function' && blockHandler(element, isCms);
				resolve();
			})
			.catch((err: any) => {
				DEBUG_VERBOSE && console.error(LOG_PREFIX, `Failed to load block component for class ${blockType}:`, err);
				resolve();
			});
	});
};

//const postProcessBlockForCms = (element: HTMLElement) => {};

const isCms: boolean = document.body.classList.contains('wp-admin') || isIframeEditor();
const activeBlocks: Set<HTMLElement> = new Set();

if (isIframeEditor()) {
	loadRequiredModules('iframe editor load', { mimicPageEnter: true, mimicPageEnterComplete: true });
	const observer = new MutationObserver((mutations) => {
		if (mutations.length > 0) {
			// For each active block, check if it's still in the DOM; if not, remove it from activeBlocks and delete its state.
			activeBlocks.forEach((block) => {
				const blockStillPresent = document.body.contains(block);
				if (!blockStillPresent) {
					activeBlocks.delete(block);
					blockLifecycleController.mimicPageLeave(block);
					blockLifecycleController.mimicPageLeaveComplete(block);
				}
			});
		}

		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				let mutationsProcessed: boolean = false;
				mutation.addedNodes.forEach((node) => {
					// The node will likely be a wp-block container, so check its children for cds-blocktype elements.
					// At this point, a placeholder block could be in the DOM instead of one of ours, if ours exits
					// emitting no HTML, as a result of an empty-block checking logic.
					const htmlNode = node as HTMLElement;
					if (htmlNode && htmlNode.querySelectorAll) {
						// Not all blocks have a cds-blocktype to be checked; 'feature' blocks that use webcomponents,
						// should not need the cds-lifecycle methods seen in core blocks. But we still want to mark the detection
						// of an HTML node mutation (with mutationsProcessed) to trigger other processes.
						mutationsProcessed = true;
						htmlNode.querySelectorAll(blocktypeQuery).forEach((el) => {
							const firePageEnterEvents: boolean = true;
							processElementBlocktype(el as HTMLElement, firePageEnterEvents);
							//convertClassnameAttributes();
						});
					} else {
						//console.log('Node is invalid: ', node);
					}
				});
				if (mutationsProcessed) {
					convertClassnameAttributes();
					scanDomForComponentTags().forEach((tag) => loadComponentCodeForTag(tag));
				}
			}
		});

		convertClassnameAttributes();
	});
	observer.observe(document, { childList: true, subtree: true });
}

document.addEventListener(PageEvent.enter, (event: any) => {
	const incomingPage: HTMLElement = event?.detail?.next?.container;

	// When loading modules in response to a PageEvent.enter, we need to mimic
	// the enter event to the blocks whose code is only imported as a response to this.
	// For the PageEnterComplete event, we only need to mimic this if specified in the event detail,
	// which will be on hard page load, when we mimic Barba's page events by actually dispatching them manually.
	loadRequiredModules(PageEvent.enter, {
		pageElement: incomingPage,
		mimicPageEnter: true,
		mimicPageEnterComplete: event?.detail?.mimic === true,
	});
});

const observer = new MutationObserver((mutations) => {
	for (const mutation of mutations) {
		for (const node of mutation.addedNodes) {
			if (node.nodeType !== Node.ELEMENT_NODE) continue;
			const el = node as HTMLElement;
			// Only run if the node itself is a relevant custom element or contains any
			if (el.tagName.toLowerCase().startsWith(prefix + '-') || el.querySelector(selector)) {
				loadComponentCodeForTag(el.tagName);
			}
		}
	}
});

observer.observe(document.body, { childList: true, subtree: true, attributes: false, characterData: false });

const shadowRootObserver = new MutationObserver((mutations) => {
	mutations.forEach((mutation) => {
		if (mutation.type != 'childList') return;
		mutation.addedNodes.forEach((node) => {
			if (node.nodeType === Node.ELEMENT_NODE) {
				const target = mutation.target as ShadowRoot;
				const parentComponent = target.host as Component;
				if (parentComponent) {
					const parentTag = parentComponent.tagName.toLowerCase();
					DEBUG_VERBOSE && console.log(LOG_PREFIX, `${parentTag} ShadowRoot mutation:`, node);
				}
				if (node instanceof HTMLElement) {
					node.querySelectorAll?.('*').forEach((el) => {
						// For each element in the shadow DOM, check for custom elements with our prefix
						const tag: string = el.tagName.toLowerCase();
						if (tag.startsWith(prefix + '-') && !customElements.get(tag)) loadComponentCodeForTag(tag);
					});
				}
				// Handle the case where the node itself is a custom element
				const tag: string = (node as HTMLElement).tagName.toLowerCase();
				if (!customElements.get(tag) && tag.startsWith(prefix + '-')) loadComponentCodeForTag(tag);
			}
		});
	});
});

// We can't just detect webcomponents by the presence of their shadowRoot property early in lifecycle,
// as the webcomponents are connected at the browser's discretion; we respond to this using an event.
// We could use a customElements.whenDefined() approach, but we can also use our own event.
document.addEventListener(ComponentEvent.CONNECTED, (event: any) => {
	const component: Component = event.detail as Component;
	if (component?.shadowRoot) {
		shadowRootObserver.observe(component.shadowRoot, { childList: true, subtree: true });
	}
	if (isCms) loadRequiredModules('Component connected');
});
document.addEventListener('component-disconnected', (event: any) => {
	const component: Component = event.detail as Component;
	if (component?.shadowRoot) {
		shadowRootObserver.disconnect();
	}
});

export function convertClassnameAttributes() {
	// With the Gutenberg editor being housed within a React app, class attributes on
	// custom elements are converted to "classname", and we need to undo this to avoid
	// alternatives to 'class', which is especially difficult when using multiple, more
	// atomic classes on a single element.
	const classnameItems = document.querySelectorAll('[classname]');
	//if (classnameItems.length) console.log('Classname item: ', classnameItems);
	classnameItems.forEach((item) => {
		const classNameValue = item.getAttribute('classname');
		classNameValue && item.setAttribute('class', classNameValue);
	});
}
